import { Id, useLaravelCrudEntity } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { clippingsService } from "../shared/api/clippingsService";
import { IClippingFormValues, IClippingRequest } from "../shared/types";

const mapToRequest = (formValues: IClippingFormValues): IClippingRequest => ({
	title: formValues.title,
	body: formValues.body,
	link: formValues.link,
	publishedAt: formValues.publishedAt,
	contactId: formValues.contact?.id || null,
	eventId: formValues.event?.id || null,
	companyId: formValues.company?.id || null,
	reach: formValues.reach,
	mediaEquivalenceValue: formValues.mediaEquivalenceValue,
});

export const useClippings = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const crudOperations = useLaravelCrudEntity(clippingsService, {
		entityName: t("Clipping"),
		messages: {
			create: t("Clipping was successfully created."),
			update: t("Clipping was successfully updated."),
			delete: t("Clipping was successfully deleted."),
		},
	});

	const reloadClippings = useCallback(() => {
		void queryClient.resetQueries({ predicate: ({ queryKey }) => queryKey.includes("clippings") });
		void queryClient.resetQueries({
			predicate: ({ queryKey }) => queryKey.includes("events") && queryKey.includes("contacts"),
		});
		void queryClient.resetQueries({ predicate: ({ queryKey }) => queryKey.includes("clipping-provider-contacts") });
	}, [queryClient]);

	const createClipping = useCallback(
		async (formValues: IClippingFormValues) => {
			const result = await crudOperations.createEntity(mapToRequest(formValues));

			if (result) {
				void reloadClippings();
			}

			return result;
		},
		[crudOperations, reloadClippings],
	);

	const updateClipping = useCallback(
		async (id: Id, formValues: IClippingFormValues, shouldRedirect?: boolean) => {
			const result = await crudOperations.updateEntity(id, mapToRequest(formValues));

			if (result) {
				void reloadClippings();
				shouldRedirect && navigate("/clippings");
			}

			return result;
		},
		[crudOperations, reloadClippings, navigate],
	);

	const deleteClipping = useCallback(
		async (id: Id) => {
			const result = await crudOperations.deleteEntity(id);

			if (result) {
				void reloadClippings();
			}

			return result;
		},
		[crudOperations, reloadClippings],
	);

	const deleteFile = useCallback(
		async (id: Id) => {
			const result = await clippingsService.deleteFile(id);

			if (result) {
				void queryClient.invalidateQueries({ queryKey: clippingsService.showKey(id) });
			}
		},
		[queryClient],
	);

	return {
		createClipping,
		updateClipping,
		deleteClipping,
		deleteFile,
	};
};
